import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { StyledEditDialog } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { toaster } from 'components/v2/toast';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { CustomValidationRuleModel } from 'services/api/adminValidationRulesApi';
import { useDeleteValidationRule, useUpsertValidationRule } from 'services/api/mutations';
import { useValidationRules } from 'services/api/queries';
import { Container } from 'styles/Styles.styled';
import { H1NewUI, FormPanel } from 'styles/v2/Styles.styled';
import styled from 'styled-components';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
`;

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 0;
  overflow: hidden;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    font-size: 13px;
    color: ${(props: any) => props.theme.colors.primaryBlack};
  }
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 12px;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const emptyRule: CustomValidationRuleModel = {
  ruleName: '',
  severity: '',
  isEnabled: true,
  activeFrom: null,
  activeTo: null,
};

const AdminValidationRules: React.FunctionComponent = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<CustomValidationRuleModel>(emptyRule);
  const [isNew, setIsNew] = useState(true);

  const rulesQuery = useValidationRules();
  const upsertMutation = useUpsertValidationRule();
  const deleteMutation = useDeleteValidationRule();

  const rules = rulesQuery.data;

  const openAddDialog = () => {
    setEditingRule({ ...emptyRule });
    setIsNew(true);
    setShowDialog(true);
  };

  const openEditDialog = (rule: CustomValidationRuleModel) => {
    setEditingRule({ ...rule });
    setIsNew(false);
    setShowDialog(true);
  };

  const handleSave = () => {
    if (!editingRule.ruleName.trim()) {
      toaster({ severity: 'error', summary: 'Validation', details: 'Rule name is required' });
      return;
    }

    upsertMutation.mutate(editingRule, {
      onSuccess: () => {
        setShowDialog(false);
        toaster({
          severity: 'success',
          summary: 'Success',
          details: isNew
            ? 'Rule created. Rules cache will be updated in 5min.'
            : 'Rule updated. Rules cache will be updated in 5min.',
        });
      },
      onError: () => {
        toaster({ severity: 'error', summary: 'Error', details: 'Failed to save rule' });
      },
    });
  };

  const handleDelete = (rule: CustomValidationRuleModel) => {
    confirmDelete({
      caption: `Are you sure you want to delete rule "${rule.ruleName}"?`,
      description: (
        <div>
          This rule will be permanently deleted.
          <br /> You will not be able to recover it.
        </div>
      ),
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () =>
        deleteMutation.mutate(rule.ruleName, {
          onSuccess: () => {
            toaster({ severity: 'success', summary: 'Success', details: 'Rule deleted' });
          },
          onError: () => {
            toaster({ severity: 'error', summary: 'Error', details: 'Failed to delete rule' });
          },
        }),
    });
  };

  const enabledBodyTemplate = (rowData: CustomValidationRuleModel) => {
    return <InputSwitch checked={rowData.isEnabled} disabled />;
  };

  const dateBodyTemplate = (value: string | null) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString();
  };

  const actionsBodyTemplate = (rowData: CustomValidationRuleModel) => {
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => openEditDialog(rowData)} />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDelete(rowData)}
        />
      </div>
    );
  };

  const parseDate = (value: string | null): Date | null => {
    if (!value) return null;
    return new Date(value);
  };

  const toISOOrNull = (value: Date | null): string | null => {
    if (!value) return null;
    return value.toISOString();
  };

  if (rulesQuery.isLoading) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ConfirmDialog />
      <PageHeader>
        <H1NewUI>Validation Rules</H1NewUI>
        <Button label="Add rule" icon="pi pi-plus" className="p-button-sm" onClick={openAddDialog} />
      </PageHeader>

      <GridPanel>
        <DataTableStyled
          value={rules || []}
          paginator
          rows={20}
          sortField="ruleName"
          sortOrder={1}
          emptyMessage="No rules found"
        >
          <Column field="ruleName" header="Rule Name" sortable style={{ minWidth: '14rem' }} />
          <Column field="severity" header="Severity" sortable style={{ minWidth: '8rem' }} />
          <Column field="isEnabled" header="Enabled" body={enabledBodyTemplate} sortable style={{ minWidth: '6rem' }} />
          <Column
            field="activeFrom"
            header="Active From"
            body={(row) => dateBodyTemplate(row.activeFrom)}
            sortable
            style={{ minWidth: '8rem' }}
          />
          <Column
            field="activeTo"
            header="Active To"
            body={(row) => dateBodyTemplate(row.activeTo)}
            sortable
            style={{ minWidth: '8rem' }}
          />
          <Column body={actionsBodyTemplate} style={{ minWidth: '6rem' }} />
        </DataTableStyled>
      </GridPanel>

      <StyledEditDialog
        visible={showDialog}
        isOpened={showDialog}
        header={isNew ? 'Add Validation Rule' : 'Edit Validation Rule'}
        style={{ width: '500px' }}
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={handleSave} loading={upsertMutation.isPending} />
          </div>
        }
      >
        <FormGrid>
          <FormField>
            <label htmlFor="ruleName">Rule Name *</label>
            <InputText
              id="ruleName"
              value={editingRule.ruleName}
              onChange={(e) => setEditingRule({ ...editingRule, ruleName: e.target.value })}
              disabled={!isNew}
            />
          </FormField>
          <FormField>
            <label htmlFor="severity">Severity</label>
            <InputText
              id="severity"
              value={editingRule.severity || ''}
              onChange={(e) => setEditingRule({ ...editingRule, severity: e.target.value || null })}
            />
          </FormField>
          <SwitchRow>
            <InputSwitch
              id="isEnabled"
              checked={editingRule.isEnabled}
              onChange={(e) => setEditingRule({ ...editingRule, isEnabled: e.value ?? false })}
            />
            <label htmlFor="isEnabled">Enabled</label>
          </SwitchRow>
          <FormField>
            <label htmlFor="activeFrom">Active From</label>
            <Calendar
              id="activeFrom"
              value={parseDate(editingRule.activeFrom)}
              onChange={(e) => setEditingRule({ ...editingRule, activeFrom: toISOOrNull(e.value as Date | null) })}
              showIcon
              showButtonBar
              dateFormat="yy-mm-dd"
            />
          </FormField>
          <FormField>
            <label htmlFor="activeTo">Active To</label>
            <Calendar
              id="activeTo"
              value={parseDate(editingRule.activeTo)}
              onChange={(e) => setEditingRule({ ...editingRule, activeTo: toISOOrNull(e.value as Date | null) })}
              showIcon
              showButtonBar
              dateFormat="yy-mm-dd"
            />
          </FormField>
        </FormGrid>
      </StyledEditDialog>
    </Container>
  );
};

export default withGreyBackground(AdminValidationRules);
